import './styles.scss';

import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import LazyLoad, { forceCheck as lazyloadForceCheck } from 'react-lazyload';
import VisibilitySensor from 'react-visibility-sensor';
import { isBrowser } from 'Helpers/Browser';
import { getRange } from 'Helpers/Select';

import GalleryLink from 'Components/layout/GalleryLink';
import GalleryOverlay from 'Components/layout/GalleryOverlay';
import GalleryOverlayV2 from 'Components/layout/GalleryOverlayV2';
import GalleryControls from 'Components/layout/GalleryControlsV2';
import GalleryPromote from 'Components/layout/GalleryPromote';
import GallerySelect from 'Components/layout/GallerySelect';
import Picture from 'Components/layout/Picture';
import Spinner from 'Components/layout/Spinner';
import LazyLoadContainer from 'Components/layout/LazyLoadContainer';

export default class Gallery extends React.Component {
    static propTypes = {
        isLoading: PropTypes.bool,
        noResultInfo: PropTypes.element,
        overlayType: PropTypes.string,
        onHover: PropTypes.func,
        onEnter: PropTypes.func,
        onEnterAsync: PropTypes.func,

        photos: PropTypes.arrayOf(
            PropTypes.shape({
                imageUrl: PropTypes.string,
                resolutions: PropTypes.object,
                width: PropTypes.number.isRequired,
                height: PropTypes.number.isRequired,
                alt: PropTypes.string,
                href: PropTypes.string,
                controls: PropTypes.object,
                overlay: PropTypes.shape({
                    type: PropTypes.string,
                    contentArray: PropTypes.array
                }),
            })
        ),

        selectable: PropTypes.shape({
            enabled: PropTypes.bool,
            visibleOnHover: PropTypes.bool,
            onStateChange: PropTypes.func,
            items: PropTypes.array
        }),

        promotable: PropTypes.shape({
            enabled: PropTypes.bool,
            controls: PropTypes.object
        }),

        groupable: PropTypes.shape({
            enabled: PropTypes.bool,
            groupBy: PropTypes.string
        })
    };
    static defaultProps = {
        isLoading: false,
        photos: []
    };

    keys = {};
    state = {
        photoMinHeight: 225,
        lazyLoadOffset: 300,
        selectDirection: null
    };

    componentDidMount() {
        if (isBrowser()) {
            document.addEventListener("keydown", event => this.keys[event.keyCode] = true);
            document.addEventListener("keyup", event => this.keys[event.keyCode] = false);
        }
    }

    componentWillUnmount = () => {
        if (isBrowser()) {
            document.removeEventListener("keydown", event => this.keys[event.keyCode] = false);
            document.removeEventListener("keyup", event => this.keys[event.keyCode] = false);
        }
    }

    componentWillReceiveProps = nextProps => {
        if (isBrowser()) {
            //Hack to lazy load photos on tab change
            lazyloadForceCheck();
        }
    }

    onHover = photo => {
        if (this.props.onHover && window && window.innerWidth >= 1024) {
            this.props.onHover(photo);
        }
    }

    getGalleryGroups = (photos, groupBy) => {
        let groups = {};
        photos.forEach(photo => {
            let date = moment(photo[groupBy]).format('MMM D, YYYY');
            if (!groups.hasOwnProperty(date)) {
                groups[date] = [];
            }

            groups[date].push(photo);
        });

        return Object.keys(groups).map(groupName => (
            <div className="gallery-group" key={groupName} id={groupName}>
                <span className="gallery-group-name">{groupName}</span>
                <div className="gallery-group-photos">
                    {this.getGalleryPhotos(groups[groupName])}
                </div>
            </div>
        ));
    }

    onSelect = selectTarget => {
        let { items } = this.props.selectable;
        const { photos } = this.props;
        let { selectDirection } = this.state;

        if (this.keys[16] && this.keys[16] === true) {
            const rangeData = getRange(photos, items, selectTarget, selectDirection);
            selectDirection = rangeData.selectDirection;
            items = rangeData.selectedItems;
        } else {
            let selectedIndex = items.findIndex(selectedItem => selectedItem.id === selectTarget.id);
            if (selectedIndex !== -1) {
                items.splice(selectedIndex, 1);
            } else {
                items.push(selectTarget);
            }
        }

        this.setState({
            ...this.state,
            selectDirection: selectDirection
        });

        this.props.selectable.onStateChange(items);
    }

    getGalleryPhotos = photos => {
        let { photoMinHeight, lazyLoadOffset } = this.state;
        const { selectable, promotable } = this.props;

        const isPortaitsOnlyGallery = photos.filter(photo => photo.width >= photo.height).length === 0;
        const multiplier = isPortaitsOnlyGallery ? 1.4 : 1;

        return (
            photos.map(photo => {
                let width = photo.width;
                let height = photo.height >= photoMinHeight ? photo.height : photoMinHeight;
                let realWidth = (width * photoMinHeight) / photo.height;
                let photoWidth = realWidth > 640 ? 640 : realWidth;

                if (isBrowser() && window.innerWidth < 640) {
                    photoMinHeight = 120;
                } else if (isBrowser() && window.innerWidth < 960) {
                    photoMinHeight = 250;
                }

                return (
                    <div
                        key={photo.id}
                        className={`picture-wrapper ${photo.removed ? 'removed' : ''} ${photo.isPromoted ? 'promoted' : ''} ${photo.isRejected ? 'rejected' : ''} ${photo.isDemoted ? 'demoted' : ''}`}
                        id={`photo-${photo.id}`}
                        onMouseEnter={() => { this.onHover(photo); }}
                        style={{
                            width: photoWidth * multiplier + 'px',
                            flexGrow: photoWidth * multiplier,
                            flexBasis: realWidth * multiplier + 'px'
                        }}
                    >
                        <i
                            className="placeholder"
                            style={{ paddingBottom: (photo.height / photo.width * 100) + '%' }}
                        >
                        </i>

                        {promotable && promotable.enabled ?
                            <GalleryPromote
                                photo={photo}
                                inSelectMode={Boolean(selectable && selectable.enabled && selectable.items.length)}
                            />
                            : null}

                        {photo.controls ?
                            <GalleryControls
                                photo={photo}
                            />
                            : null}

                        {selectable && selectable.enabled ?
                            <GallerySelect
                                photo={photo}
                                visibleOnHover={selectable.visibleOnHover || false}
                                onSelect={this.onSelect}
                                isSelected={this.props.selectable.items.findIndex(selectedItem => selectedItem.id === photo.id) !== -1}
                            />
                            : null}

                        <GalleryLink
                            href={selectable && selectable.enabled && this.props.selectable.items.length ? null : photo.href}
                            onEnter={selectable && selectable.enabled && this.props.selectable.items.length ? () => this.onSelect(photo) : this.props.onEnter ? () => this.props.onEnter(photo) : null}
                            onEnterAsync={this.props.onEnterAsync ? () => this.props.onEnterAsync(photo) : null}
                        >
                            {!photo.overlay ? <GalleryOverlay photo={photo} type={this.props.overlayType} /> : null}
                            {photo.overlay ? <GalleryOverlayV2 {...photo.overlay} /> : null}
                            <LazyLoad height={(photo.height / photo.width * 100)} offset={lazyLoadOffset} resize={true}>
                                <Picture
                                    url={photo.imageUrl}
                                    resolutions={photo.resolutions}
                                    alt={photo.selectedCaption}
                                />
                            </LazyLoad>
                        </GalleryLink>
                    </div>
                );
            })
        );
    }

    render() {
        const { photoMinHeight } = this.state;
        const { photos, selectable, isLoading, noResultInfo, promotable, groupable } = this.props;

        let content = null;
        if (isLoading) {
            content = (<Spinner className="search-spinner" />);
        } else if (!photos.length && noResultInfo) {
            content = (noResultInfo);
        } else {
            if (groupable && groupable.enabled && groupable.groupBy) {
                content = this.getGalleryGroups(photos, groupable.groupBy);
            } else {
                content = this.getGalleryPhotos(photos);
            }
        }

        return (
            <div className={`component-gallery ${selectable && selectable.enabled && selectable.items.length ? 'select-mode' : ''}`}>
                <VisibilitySensor onChange={offset => this.setState({ ...this.state, lazyLoadOffset: 300 }, () => lazyloadForceCheck())}>
                    <LazyLoadContainer baseOffset={300} onOffsetChange={offset => this.setState({ ...this.state, lazyLoadOffset: offset }, () => lazyloadForceCheck())}>
                        <div className={`gallery-container`}>
                            {content}
                        </div>
                    </LazyLoadContainer>
                </VisibilitySensor>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/layout/Gallery/index.js